<script>
import Cropper from 'cropperjs';
import animate from '@/modules/animate-css';

export default {
  name: 'ImageInput',
  props: {
    aspectRatio: {
      type: Number,
      default: 1
    },
    crop: Boolean,
    dark: Boolean,
    defaultImage: String,
    disabled: Boolean,
    height: {
      type: Number,
      default: 128
    },
    imageMaxHeight: {
      type: String,
      default: '100%'
    },
    imageMaxWidth: {
      type: String,
      default: '100%'
    },
    title: String,
    value: String
  },
  data() {
    return {
      image: this.value,
      dialogVisible: false,
      isDragging: false
    };
  },
  watch: {
    value(val) {
      this.image = val;
    }
  },
  methods: {
    // Called when the user selects a new image
    changeImage(file) {
      if (this.disabled) {
        return;
      }

      // Is the file an image?
      if (!file || !file.type.match(/^image\//)) {
        animate(this.$refs.imageInput, 'shake', 1);
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.addEventListener('load', () => {
        const dataURL = reader.result;
        if (this.crop) {
          this.cropImage(dataURL);
        } else {
          // Convert to desired format
          this.image = dataURL;
          this.$emit('input', this.image);
        }
      });

      // Reset the file input so subsequent selections fire the change event
      this.$refs.fileInput.value = '';
    },

    // Opens the crop dialog
    cropImage(dataURL) {
      this.dialogVisible = true;
      // We have to wait for the dialog to render, so we use $nextTick after setting dialogVisible
      // before initializing the cropper
      this.$nextTick(() => {
        this.$refs.cropperImage.src = dataURL;
        this.cropper = new Cropper(this.$refs.cropperImage, {
          aspectRatio: this.aspectRatio,
          autoCropArea: 0.85,
          dragMode: 'move',
          center: true,
          guides: false,
          cropBoxMovable: false,
          cropBoxResizable: false,
          minCropBoxWidth: 16,
          minCropBoxHeight: 16,
          movable: true,
          toggleDragModeOnDblclick: false,
          viewMode: 1,
          zoomable: true
        });
      });
    },

    // Applies the cropped image to the control
    setCroppedImage() {
      const dataURL = this.cropper.getCroppedCanvas().toDataURL('image/png');
      this.image = dataURL;
      this.$emit('input', this.image);
    },

    // Destroy the cropper when the dialog is hidden
    dialogHidden() {
      this.cropper.destroy();
    },

    // Removes the image
    removeImage() {
      this.image = null;
      this.$emit('input', this.image);
    },

    // Rotates the cropper
    rotate(angle) {
      this.cropper.rotate(angle);
    },

    // Rotates the cropper
    zoom(amount) {
      this.cropper.zoom(amount);
    }
  }
};
</script>

<template>
  <div
    ref="imageInput"
    :class="{
      'image-input': true,
      'image-input-dark': dark,
      'is-disabled': disabled
    }"
    :style="`height: ${height}px`"
  >
    <div
      :class="{
        'image-input-body': true,
        'is-dragging': !disabled && isDragging
      }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="isDragging = false; changeImage($event.dataTransfer.files[0])"
    >
      <el-dropdown trigger="click">
        <el-button
          :disabled="disabled"
          :title="title"
          class="image-input-button"
        >
          <img
            :src="image ? image : (defaultImage ? defaultImage : require('@/assets/image-light.svg'))"
            :style="{
              maxWidth: imageMaxWidth,
              maxHeight: imageMaxHeight
            }"
            class="image-input-image"
          >
        </el-button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item>
            <label class="image-input-file-label">
              {{ $t('Choose Image') }}
              <input
                ref="fileInput"
                type="file"
                style="display: none;"
                @change="changeImage($event.target.files[0])"
              >
            </label>
          </el-dropdown-item>
          <el-dropdown-item
            :disabled="!image"
            divided
            @click.native="removeImage"
          >
            {{ $t('Remove') }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- Crop dialog -->
    <el-dialog
      :title="$t('Position Your Photo')"
      :visible.sync="dialogVisible"
      @closed="dialogHidden"
    >
      <div class="cropper">
        <img ref="cropperImage">
      </div>

      <div class="image-input-options">
        <el-button-group>
          <el-button :title="$t('Zoom')" size="small" @click="zoom(0.1);">
            <i class="el-icon-fa-search-plus" />
          </el-button>
          <el-button :title="$t('Zoom')" size="small" @click="zoom(-0.1);">
            <i class="el-icon-fa-search-minus" />
          </el-button>
          <el-button :title="$t('Rotate')" size="small" @click="rotate(90);">
            <i class="el-icon-fa-redo" />
          </el-button>
          <el-button :title="$t('Rotate')" size="small" @click="rotate(-90);">
            <i class="el-icon-fa-undo" />
          </el-button>
        </el-button-group>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">
          {{ $t('Cancel') }}
        </el-button>
        <el-button type="primary" @click="setCroppedImage(); dialogVisible = false">
          {{ $t('Apply') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.image-input {
  position: relative;
  margin-bottom: 1rem;
}

.image-input-dark .image-input-body .el-button {
  background-color: $--color-gray-90;
  border-color: $--color-gray-90;

  &:hover:not(.is-disabled),
  &:focus:not(.is-disabled) {
    background-color: $--color-gray-80;
    border-color: $--color-gray-80;
  }
}

.image-input-body {
  height: 100%;

  img {
    max-width: 100%;
    max-height: 100%;
    border-radius: 0.175rem;
    vertical-align: middle;
    margin: 0 auto;
    transition: 0.2s all;
  }

  &.is-dragging img {
    opacity: 0.5;
  }

  .el-dropdown {
    width: 100%;
    height: 100%;
  }

  .el-button {
    width: 100%;
    height: 100%;
    padding: 0;
    transition: 0.2s background-color;
  }

  &.is-dragging .el-button {
    background-color: $--color-primary;
  }
}

.image-input.is-disabled .image-input-body img {
  opacity: 0.5;
}

.image-input-file-label {
  display: block;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  cursor: pointer;
}

.image-input-options {
  text-align: center;
  margin-bottom: 1rem;
}

.cropper {
  width: 100%;
  max-height: 18rem;
  margin: 0 auto 1rem auto;
}
</style>
